import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./UploadPortfolioModal.module.scss";
import CrossIconSVG from "components/SVGComponents/CrossIconSVG";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import {
  getUserProfile,
  profileCreation,
  resetPortfolioURL,
  setPortfolioURL,
  uploadProfilePortfolio,
} from "reducer/profileSlice";
import { useDispatch } from "react-redux";
import PDFPreview from "components/common/PDFPreview/PDFPreview";
import { serverFiles } from "utils/utils";
import { applyJob } from "reducer/jobSlice";
import { useLocation, useNavigate } from "react-router-dom";

const UploadPortfolioModal = ({
  applyBtn,
  userProfile,
  isOpen,
  onClose,
  portfolioModal,
  setPortfolioModal,
  setAppliedModal,
  applied,
  setApplied,
  projectId,
}) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState(
    userProfile?.additionalLink?.length ? userProfile?.additionalLink[0] : ""
  );
  const [previewURL, setPreviewURL] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);

  const applyBtnRef = useRef(0);
  const delay = 1000; // 1 second delay

  const navigate = useNavigate();
  const location = useLocation();
  // console.log('location', location)

  const handleUpload = async () => {
    if (!userProfile?.isCompleted) {
      navigate(`/profile-creation?redirect=${location.pathname}`);
    } else {
      const now = Date.now();

      if (now - applyBtnRef.current >= delay) {
        if (!userProfile?.portfolioUrl && !file && !url) {
          toast.error("Please upload a portfolio or provide a URL");
          return;
        }

        if (url && !isValidPortfolioLink(url)) {
          toast.error("Invalid link! Please provide a valid URL.");
          return;
        }

        let urlResponse;
        let fileResponse;
        let categoryId = userProfile?.categories?.map((item) => item.id);

        try {
          const alreadyHasFile = !!userProfile?.portfolioUrl && !file;
          const alreadyHasUrl =
            url && userProfile?.additionalLink?.includes(url);

          // Upload new file if provided and not already present
          if (file && !alreadyHasFile) {
            setUploading(true);
            const formData = new FormData();
            formData.append("file", file);
            fileResponse = await dispatch(
              uploadProfilePortfolio(formData)
            ).unwrap();
            setUploading(false);
          }

          // Add new URL if provided and not already present
          if (url && !alreadyHasUrl) {
            urlResponse = await dispatch(
              profileCreation({
                ...userProfile,
                categoryId,
                additionalLink: [url],
              })
            ).unwrap();
          }

          // If file/url already exist OR updates succeeded → apply for job
          if (alreadyHasFile || alreadyHasUrl || urlResponse || fileResponse) {
            setApplying(true);
            await dispatch(applyJob(projectId)).unwrap();
            setApplying(false);
            setPortfolioModal(false);
            setAppliedModal(true);
            setApplied(true);
          }
        } catch (error) {
          console.error("Upload/apply error:", error);
          toast.error("Something went wrong while uploading or applying");
        } finally {
          setUploading(false);
        }
      }
    }
  };

  const isValidPortfolioLink = (link) => {
    if (!link) return false;

    // General URL regex (supports http, https, subdomains, query params, etc.)
    const urlRegex = /^(https?:\/\/)[\w.-]+(\.[\w.-]+)+[/#?]?.*$/i;

    return urlRegex.test(link);
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;

      setUploading(true);
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      const newURL = URL.createObjectURL(selectedFile);
      setPreviewURL(newURL);
      setUploading(false);
    },
    [dispatch]
  );

  // useEffect(() => {
  //   dispatch(getUserProfile());
  // }, [1]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      // "application/msword": [".doc", ".docx"],
      // "text/csv": [".csv"],
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
  });

  const removeFile = () => {
    setFile(null);
    setProgress(0);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal}>
      <div className={styles.top}>
        {/* Title */}
        <h2 className={styles.title}>Upload Your Portfolio</h2>

        {/* Close Button */}
        <button onClick={onClose} className={styles.closeBtn}>
          <CrossIconSVG color="black" height="24" width="24" />
        </button>
      </div>
      {/* Upload Area */}
      {uploading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "5rem",
            gap: "0.5rem",
          }}
        >
          {/* Spinner */}
          <div
            style={{
              width: "30px",
              height: "30px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #6570FC",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>

          {/* Text */}
          <span
            style={{ fontSize: "14px", fontWeight: "500", color: "#6570FC" }}
          >
            Uploading...
          </span>

          {/* Inline Keyframes */}
          <style>
            {`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}
          </style>
        </div>
      ) : userProfile?.portfolioUrl || previewURL ? (
        <div className={styles.pdf_Preview}>
          <div className={styles.pdf_div}>
            <PDFPreview
              classname={styles.pdf}
              url={
                previewURL ? previewURL : serverFiles(userProfile?.portfolioUrl)
              }
            />
            <button
              type="button"
              className={styles.portfolioDocument_change_button}
              onClick={(e) => {
                e.preventDefault();
                // dispatch(uploadProfilePortfolio(null));
                dispatch(resetPortfolioURL());
                setPreviewURL("");
              }}
            >
              <CrossIconSVG color="red" />
            </button>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          // onClick={() => document.getElementById("file-upload").click()}
          className={styles.uploadBox}
        >
          <input
            {...getInputProps()}
            //   id="file-upload"
            //   type="file"
            //   accept=".doc,.pdf,.csv"
            //   //   onChange={onDrop}
            //   className={styles.hiddenInput}
          />
          <div className={styles.uploadIcon}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="40" height="40" rx="20" fill="#6570FC" />
              <path
                d="M20 13.25V22.25"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23.75 17L20 13.25L16.25 17"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M26.75 22.25V25.25C26.75 25.6478 26.592 26.0294 26.3107 26.3107C26.0294 26.592 25.6478 26.75 25.25 26.75H14.75C14.3522 26.75 13.9706 26.592 13.6893 26.3107C13.408 26.0294 13.25 25.6478 13.25 25.25V22.25"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className={styles.heading}>
            Drag & Drop or <span className={styles.link}>Choose file</span> to
            upload
          </p>
          <small className={styles.supported}>
            Supported Formats: PDF (Upto 10 MB)
          </small>
        </div>
      )}
      <div className={styles.urlUpload}>
        <label htmlFor="" className={styles.inputLabel}>
          URL
        </label>
        <input
          type="text"
          placeholder="Paste link here"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          // onChange={(e) => {
          //   const newUrl = e.target.value.trim();
          //   setUrl(newUrl);

          //   if (newUrl && !isValidPortfolioLink(newUrl)) {
          //     toast.error(
          //       "Please enter a valid Google Drive, Dropbox, or OneDrive link"
          //     );
          //   }
          // }}
        />
      </div>
      {/* Action Buttons */}
      <div className={styles.actions}>
        <button className={styles.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button
          className={styles.uploadBtn}
          onClick={handleUpload}
          type="button"
          disabled={
            uploading || (!userProfile?.portfolioUrl && !previewURL && !url)
          }
        >
          {uploading
            ? "Uploading..."
            : userProfile?.portfolioUrl || previewURL
            ? applying
              ? "Applying..."
              : "Apply"
            : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default UploadPortfolioModal;
